import { useCallback, useEffect, useState } from 'react';
import { apiClient } from '../../../api/apiClient';

/**
 * 회사별 마케터 탭 — 담당 마케터 목록과 연결 추가/해제 (2026-09-08, P3).
 *
 *   멤버십의 정본은 marketer_company_links 다. 계정은 1인 1개이고, 3사를 도는 마케터는
 *   같은 계정 id 로 세 탭에 모두 나타난다(linked_companies 로 어느 회사를 도는지 표시).
 *   마케터 등록·수정·퇴사 자체는 기존 마케팅명부 라우트(/api/admin/sales-people)를 그대로 쓴다.
 */
export function useMarketerLinks({ companyCode, includeResigned, showToast, enabled = true }) {
  const [marketers, setMarketers] = useState([]);
  const [loading, setLoading] = useState(false);
  // 연결 추가 후보 = 명부 전체에서 이 회사에 아직 연결 안 된 사람
  const [candidates, setCandidates] = useState([]);

  const fetchMarketers = useCallback(async () => {
    if (!enabled || !companyCode) return;
    setLoading(true);
    const result = await apiClient.get(
      `/api/admin/marketer-links/companies/${encodeURIComponent(companyCode)}?include_resigned=${includeResigned}`,
    );
    if (result.ok) setMarketers(result.data.list || []);
    else {
      setMarketers([]);
      showToast(result.data?.message || '마케터 목록을 불러오지 못했습니다.', 'error');
    }
    setLoading(false);
  }, [companyCode, includeResigned, enabled, showToast]);

  // 후보 목록은 마케터 링크 라우트에서 가져온다 (2026-09-09).
  //   전에는 마케팅명부 목록 API 를 썼는데 그건 명부 권한을 요구해서, 회사별 직원관리
  //   담당자(김지은·원유학·이승언)는 403 을 받았다. 게다가 실패를 조용히 빈 배열로 바꿔서
  //   화면에 "추가할 수 있는 마케터가 없습니다"만 떴다 — 권한 문제인 줄 알 방법이 없었다.
  const fetchCandidates = useCallback(async () => {
    if (!companyCode) return;
    const result = await apiClient.get(
      `/api/admin/marketer-links/candidates/${encodeURIComponent(companyCode)}`);
    if (result.ok) {
      setCandidates(result.data.list || []);
    } else {
      setCandidates([]);
      showToast(result.data?.message || '마케터 후보를 불러오지 못했습니다.', 'error');
    }
  }, [companyCode, showToast]);

  useEffect(() => { fetchMarketers(); }, [fetchMarketers]);

  async function linkMarketer(marketerUserId) {
    const result = await apiClient.post('/api/admin/marketer-links', {
      marketer_user_id: marketerUserId, company_code: companyCode,
    });
    if (!result.ok) {
      showToast(result.data?.message || '담당 지정에 실패했습니다.', 'error');
      return false;
    }
    showToast('담당 마케터로 지정했습니다.', 'success');
    await fetchMarketers();
    return true;
  }

  async function unlinkMarketer(marketerUserId, name) {
    if (!window.confirm(`${name} 님을 이 회사 담당에서 해제할까요?\n계정과 지난 실적은 그대로 남고, 이 회사 목록에서만 빠집니다.`)) return false;
    // apiClient.delete 는 body 를 싣지 않는다 → 서버가 함께 받는 쿼리 파라미터로 보낸다.
    const result = await apiClient.delete(
      `/api/admin/marketer-links?marketer_user_id=${encodeURIComponent(marketerUserId)}`
      + `&company_code=${encodeURIComponent(companyCode)}`,
    );
    if (!result.ok) {
      showToast(result.data?.message || '해제에 실패했습니다.', 'error');
      return false;
    }
    showToast('담당에서 해제했습니다.', 'success');
    await fetchMarketers();
    return true;
  }

  return { marketers, loading, candidates, fetchMarketers, fetchCandidates, linkMarketer, unlinkMarketer };
}
